import { useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router";
import type { DestinationOption, HomeSnapshot, JourneyMapSnapshot, JourneySuggestion } from "@/shared/contracts";
import { useServices } from "@/shared/services/registry";
import { markSuggestion, rejectKnownSuggestion, suggestionMutationKey, useDepartureScope, type DepartureSuggestRequest } from "./departureCurrentScope";
import { Button, Chip, ErrorState, Icon, LoadingState, EmptyState, avatarChain, useAvatarStep, type IconName } from "@/shared/ui";
import courtyard from "@/features/home/assets/living/courtyard-base.webp";
import { realSiteHref } from "@/features/home/RealSiteNotice";
import { PawBadge } from "@/features/pets/PetPortrait";
import { SpeciesIllustration } from "@/features/pets/SpeciesIllustration";
import { tripTitleLine } from "./tripTitle";
import { homePlaceUnchosen } from "./homePlaceCity";
import { sizedPhotoSrc, usePhotoSizes } from "@/shared/ui/photoSizes";

const MODE_ICON: Record<string, IconName> = { walk: "compass", taxi: "car", car: "car", ferry: "ship", flight: "plane", train: "train" };
const MODE_TEXT: Record<string, string> = { walk: "步行", taxi: "打车", car: "自驾", ferry: "轮渡", flight: "飞机", train: "火车" };

function minutesText(total: number): string {
  if (total < 60) return `约 ${total} 分钟`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m ? `约 ${h} 小时 ${m} 分` : `约 ${h} 小时`;
}

const SUGGESTION_TEXT: Record<string, string> = {
  pending: "你的建议已经放进 TA 心里了；去不去、什么时候去，由 TA 决定。",
  accepted: "TA 听了你的建议，去过这里了。",
  passed: "这阵子 TA 没选它，下次可以再聊聊。",
  replaced: "你后来换了一个建议。",
};

/**
 * 从没选过城的家（DEPART-CITY-GUIDE-117；见 ./homePlaceCity）：出发站照实说「还没选好 TA 住在哪」，给去选城的路（/home/real-site，
 * 那一页认得「从没选过城」，复用入住页的城市选择）。选城只有家庭管理员能做（cities.py）：管理员给「去选城」，共同照顾者说「请家里的管理员先选城」、不给按钮；
 * 老快照没有家庭信息时照旧给入口，选城页自己会照实说能不能换。
 * 同一件事一屏只说一处、只放一颗「去选城」（设计评审 R1 高、中 3，R2 中 1）：顶上那一行出了（onTop），下面的空态只说列表为什么空，
 * 一行式不再出；只有快照还说选过城、读到没选城（顶上没有那一行）时，下面才说完整的那句、放那一颗。
 * 读到没选城的出错也不当成出错（不出红色警示块、不给重试，再读也一样），换成和空态同一块平静的说明。
 * note：顶上那一行和一行式说明的后半句；listNote：列表那一块的说明；action：管理员的「去选城」。
 */
type CityGuide = { note: string; listNote: string; action: ReactNode | null; onTop: boolean };

/** 没选城时列表那一块（空目的地、读目的地读到没选城）：平静的说明；顶上那一行已经说了没选城、该找谁，这里不再重复 */
function CityGuideState({ city }: { city: CityGuide }) {
  if (city.onTop) return <EmptyState icon="home" title="还没有能去的地方">选好城以后，这里会列出来。</EmptyState>;
  return <EmptyState icon="home" title="还没选好 TA 住在哪" action={city.action}>{city.listNote}</EmptyState>;
}

/** 一行式（地点卡里、已有建议那一栏）：和顶上那一行同一个样子 */
function CityGuideLine({ city }: { city: CityGuide }) {
  return (
    <div className="ps-trip-home__unchosen">
      <p role="status"><Icon name="home" size={15} /> <span>还没选好 TA 住在哪。<span className="ps-trip-home__unchosen-note">{city.note}</span></span></p>
      {city.action}
    </div>
  );
}

/** 出错块：原因是没选城时换成上面那两种说明（block＝列表那一块，line＝一行，顶上那一行在就不出）；别的原因照旧 */
function DepartureError({ error, onRetry, city, line = false }: { error: unknown; onRetry?: () => void; city: CityGuide; line?: boolean }) {
  if (!homePlaceUnchosen(error)) return <ErrorState error={error} onRetry={onRetry} />;
  if (!line) return <CityGuideState city={city} />;
  return city.onTop ? null : <CityGuideLine city={city} />;
}

/** 一个地方的“悄悄话”：只有建议，没有替 TA 出发的按钮（出门由 TA 自主决定）。 */
function SuggestPlace({ option, petId, suggestion, departure, city }: {
  option: DestinationOption;
  petId: string;
  suggestion: JourneySuggestion | null;
  departure: ReturnType<typeof useDepartureScope>;
  city: CityGuide;
}) {
  const { transport } = useServices();
  const available = option.available !== false;
  const suggest = useMutation({
    mutationKey: suggestionMutationKey(departure.scope), gcTime: Infinity, retry: false,
    mutationFn: async (request: DepartureSuggestRequest) => {
      await departure.beforeSuggest(request); markSuggestion(request);
      return transport.suggest(request.destination, request.scope.petId, request.clientRequestId);
    },
    onSuccess: async (result, request) => { await departure.refreshAfterSuggest(request, result); },
    onError: (error, request) => { rejectKnownSuggestion(request, error); },
  });
  const pending = suggestion?.status === "pending" || departure.originalReceipt?.suggestion.destination_key === option.destination_key && departure.originalReceipt.suggestion.status === "pending";
  return (
    <article className={`ps-suggest-place${pending ? " is-pending" : ""}`}>
      <div className="ps-suggest-place__head">
        <strong>{option.title}</strong>
        <span>{option.city} · {option.modes.map((m) => MODE_TEXT[m] ?? "其他交通").join(" / ")} · 往返{minutesText(option.total_minutes)}</span>
      </div>
      <p>{option.summary}</p>
      <div className="ps-suggest-place__meta">
        {option.modes.slice(0, 1).map((m) => <Icon key={m} name={MODE_ICON[m] ?? "journey"} size={15} />)}
        <span>路费约 {option.fee} 星币</span>
        {option.wish_match ? <Chip tone="leaf" icon="bookmark">{option.wish_match}</Chip> : null}
      </div>
      {!available ? <p role="status" className="ps-suggest-place__note is-muted">{option.unavailable_reason || "这条路暂时去不了。"}</p> : null}
      {suggestion ? <p role="status" className="ps-suggest-place__note">{SUGGESTION_TEXT[suggestion.status] ?? SUGGESTION_TEXT.pending}</p> : null}
      {/* 「悄悄告诉 TA」是一列地点卡上各有一颗的动作：次按钮（UI-BASE-01a，总账 V-02；原来没建议时是叶绿按钮），一屏不出好几颗主按钮 */}
      <Button variant="secondary" size="sm" disabled={!available || pending || !departure.canSuggest || departure.unresolved} loading={suggest.isPending} onClick={() => {
        if (!departure.scope || departure.scope.petId !== petId || !departure.canSubmitNow()) return;
        suggest.mutate({ destination: option.destination_key, scope: departure.scope, guard: departure.authority });
      }}>
        {pending ? "TA 在考虑" : "悄悄告诉 TA"}
      </Button>
      {suggest.isError && departure.originalReceipt?.suggestion.destination_key !== option.destination_key ? <DepartureError error={suggest.error} city={city} line /> : null}
    </article>
  );
}

/**
 * 门廊上的 TA（整块只是装饰，读屏不念，名字写在旁边的标题里）：和全站头像同一条退回链（shared/ui 的 avatarChain）——
 * 照片 → 演示小灰猫（只在演示模式）→ 物种插画（只给领养来的原创居民，按家园快照里的来历与物种认）或爪印；照片打不开就往下退。
 */
function PorchPet({ pet }: { pet: HomeSnapshot["pet"] }) {
  const { step, next } = useAvatarStep(avatarChain(pet.photo_url, pet.origin, pet.species));
  // 照片缩略档（UI-PET-PHOTO-SIZES-02）：门廊上的 TA 最大 96px（journey.css 的 clamp），取 192 那档；没有这项能力时照旧原图
  const sized = usePhotoSizes();
  const pic = step.kind === "photo" || step.kind === "demo" ? step.src : null;
  const art = step.kind === "species" ? step.species : null;
  return (
    <span className={`ps-trip-home__pet${pic ? " has-photo" : art ? " is-species" : ""}`} aria-hidden="true" data-species={art ?? undefined}>
      {pic ? <img key={pic} src={sized ? sizedPhotoSrc(pic, 96) : pic} alt="" onError={next} /> : art ? <SpeciesIllustration species={art} /> : <PawBadge size={64} />}
    </span>
  );
}

/**
 * 在家时的旅途页。出不出门、去哪里、什么时候走由 TA 自己决定（后端生活节奏），主人只能“悄悄告诉 TA 一个地方”。
 * 不提供替 TA 立即出发的按钮：那是主人代替宠物决定，和“宠物自主”相反。
 */
export function JourneyAtHome({ home, last }: { home: HomeSnapshot; last: JourneyMapSnapshot | null }) {
  const [open, setOpen] = useState(false);
  const pet = home.pet;
  const otherGardenKeepers = (home.guard.guarding_pets ?? []).filter((id) => id !== pet.pet_id);
  const departure = useDepartureScope(home, open);
  const { destinations, suggestions } = departure;
  const pending = suggestions.state === "ready" ? suggestions.data.find(item => item.status === "pending") ?? null : null;
  // CITY-01 v4（UI-MOVEIN-CITY-01b）：城市定了、家的地址还在落定。这时 TA 不出门（出门规划要地址），落定后自动接上；页面如实说，不让空目的地看着像坏了
  const addressPending = home.place?.address_pending === true;
  // 从没选过城（见上面 homePlaceUnchosen 的说明）：顶上一行、空目的地、出错块都照实说，去选城的路一屏一颗；只有管理员能选
  const unchosen = home.place?.chosen === false;
  const canChooseCity = home.household?.role !== "caregiver";
  const cityLink = realSiteHref(pet.pet_id);
  const cityButton = canChooseCity ? <Link className="ps-btn ps-btn--primary ps-btn--sm" to={cityLink}>去选城</Link> : null;
  // 「TA」和「才会出门」之间是不断行空格：这半句只在逗号处折行（评审 R1 中 1）
  const city: CityGuide = {
    note: canChooseCity ? "选好一座城，TA\u00a0才会出门。" : "请家里的管理员先选城。",
    listNote: canChooseCity ? "选好一座城，这里会列出 TA 能去的地方。" : "请家里的管理员先选城。",
    action: cityButton,
    onTop: unchosen,
  };
  return (
    <div className="ps-trip-home">
      <section className="ps-trip-home__stage" aria-label={`${pet.name} 在家`}>
        <img className="ps-trip-home__scene" src={courtyard} alt="" />
        <PorchPet pet={pet} />
        <div className="ps-trip-home__copy">
          <span className="ps-trip-home__eyebrow"><i aria-hidden="true" />此刻 · 在家</span>
          <h2>{pet.name} 在家</h2>
          <p>出不出门、去哪里、什么时候走，都由 TA 自己决定。{home.wallet.balance < 10 ? "星币不多的时候，TA 多半就在附近走走。" : ""}</p>
          <p className="ps-trip-home__garden">{otherGardenKeepers.length > 0 ? `TA 出门时，还有 ${otherGardenKeepers.length} 位伙伴在家照看菜园。` : "TA 出门后，院子交给你照看；没人巡院时，邻居可能来摘一点。"}</p>
        </div>
      </section>
      {addressPending ? <p className="ps-trip-home__pending" role="status"><Icon name="home" size={15} /> 新家地址还在落定。落定之前，TA 先待在家里，不出门。</p> : null}
      {unchosen ? (
        <div data-testid="home-place-unchosen">
          <CityGuideLine city={city} />
        </div>
      ) : null}
      {pending ? (
        <p className="ps-trip-home__pending" role="status"><Icon name="heart" size={15} /> 你悄悄说过的「{pending.title}」，TA 还在想。</p>
      ) : null}
      {last && last.lifecycle === "completed" ? (
        <Link className="ps-trip-home__last" to="/collection">
          <Icon name="check" size={17} />
          <span>{tripTitleLine("上一趟", last.destination_title) ?? "上一趟出门"}，已经平安回家。<small>带回的东西在回忆与收藏里</small></span>
          <Icon name="chevron" size={15} />
        </Link>
      ) : null}
      <Link className="ps-departure-archive" to="/guides"><Icon name="bookmark" size={17} /> 翻看 TA 的旅行手账 <Icon name="chevron" size={15} /></Link>
      <section className="ps-trip-suggest">
        <button type="button" className="ps-trip-suggest__toggle" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
          <span><strong>悄悄告诉 TA 一个地方</strong><small>TA 会放在心上，接下来一天出门时会想起它；去不去由 TA 决定。</small></span>
          <Icon name="chevron" size={17} />
        </button>
        {open ? (
          <div className="ps-trip-suggest__list">
            {suggestions.state === "error" ? <DepartureError error={suggestions.error} onRetry={suggestions.retry} city={city} line /> : null}
            {departure.unresolved ? <p role="status" className="ps-suggest-place__note">这次建议的结果还没确认，暂时不再发送新的建议。{departure.legacyHeld ? "原来的记录无法确认，请先保留它。" : null}<Button variant="secondary" size="sm" onClick={departure.recoverOriginal} disabled={!departure.canRecover || departure.recovering} loading={departure.recovering}>查看这次建议的结果</Button></p> : null}
            {departure.recoveryError ? <p role="status" className="ps-suggest-place__note">{departure.recoveryError}</p> : null}
            {departure.originalReceipt ? <p role="status" className="ps-suggest-place__note">你说过的「{departure.originalReceipt.suggestion.title}」已经提交，去不去由 TA 自己决定。{departure.originalReceipt.note_status === "unknown" ? "附上的悄悄话是否送达还没确认，不会再次发送。" : "附上的悄悄话已送达。"}</p> : null}
            {destinations.state === "loading" ? <LoadingState /> : destinations.state === "error" ? <DepartureError error={destinations.error} onRetry={destinations.retry} city={city} /> : destinations.data.length === 0 ? (unchosen ? <CityGuideState city={city} /> : addressPending ? <EmptyState icon="home" title="新家地址还在落定">落定之后，这里会列出 TA 能去的地方。</EmptyState> : <EmptyState title="这里还空着" />) : (
                <>
                  {destinations.data.map((option) => (
                    <SuggestPlace
                      key={departure.scopeKey + ":" + option.destination_key}
                      option={option}
                      petId={pet.pet_id}
                      suggestion={suggestions.state === "ready" ? suggestions.data.find((item) => item.destination_key === option.destination_key) ?? null : null}
                      departure={departure}
                      city={city}
                    />
                  ))}
                </>
              )}
          </div>
        ) : null}
      </section>
    </div>
  );
}
